
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useSearchParams, Navigate, useLocation } from "@/lib/router-compat";
import { Calendar, Clock, ArrowRight, TrendUp as TrendingUp } from "@phosphor-icons/react";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useCountry } from "@/hooks/useCountry";
import { pageContent } from "@/utils/translations";
import { getAllBlogPosts, type BlogPostData } from "@/data/blogPosts";
import { BLOG_CATEGORIES, LEGACY_BLOG_CATEGORY } from "@/data/blogCategories";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { NewsSection } from "@/components/blog/NewsSection";


// Categories are derived dynamically from actual posts below

const Blog = () => {
  const { language } = useTranslation();
  const { country } = useCountry();
  const content = pageContent[language];
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const activeCategory = searchParams.get("category") || "";
  const rawBlogFilter = searchParams.get("blogCategory") || "Toate";
  // Linkuri vechi (?blogCategory=Ghiduri BMW etc.) → categoria nouă, cu înlocuirea parametrului.
  const legacyTarget = LEGACY_BLOG_CATEGORY[rawBlogFilter];
  const activeBlogFilter = legacyTarget ?? rawBlogFilter;
  // Legacy: /blog?post=slug → /blog/slug (301-style redirect to avoid duplicate title with hub)
  const legacyPostSlug = searchParams.get("post");
  if (legacyPostSlug) {
    return <Navigate to={`/blog/${legacyPostSlug}`} replace />;
  }
  if (legacyTarget) {
    const next = new URLSearchParams(searchParams);
    next.set("blogCategory", legacyTarget);
    return <Navigate to={`${pathname}?${next.toString()}`} replace />;
  }
  
  const blogPrefix = country === 'UK' ? '/uk/blog' : country === 'DE' ? '/de/blog' : '/blog';
  
  const blogPosts = getAllBlogPosts(country);

  // Build category list dynamically from posts (sorted by article count desc)
  const categoryCounts = blogPosts.reduce<Record<string, number>>((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {});
  // Ordine fixă (etapa 8); categoriile fără articole nu apar.
  const sortedCategories = [
    ...BLOG_CATEGORIES.filter((c) => categoryCounts[c] > 0),
    ...Object.keys(categoryCounts).filter((c) => !(BLOG_CATEGORIES as readonly string[]).includes(c)),
  ];
  const blogCategories = ["Toate", ...sortedCategories];

  const selectCategory = (cat: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (cat === "Toate") newParams.delete("blogCategory");
    else newParams.set("blogCategory", cat);
    setSearchParams(newParams);
  };

  const filteredBlogPosts = activeBlogFilter === "Toate"
    ? blogPosts
    : blogPosts.filter(post => post.category === activeBlogFilter);
  const featuredPosts = filteredBlogPosts.filter(post => post.featured);
  const regularPosts = filteredBlogPosts.filter(post => !post.featured);

  const getMetaLang = () => {
    if (country === 'UK') return 'en-GB';
    if (country === 'DE') return 'de';
    return 'ro';
  };

  const getCanonicalUrl = () => {
    if (country === 'UK') return 'https://roautomarket.ro/uk/blog';
    if (country === 'DE') return 'https://roautomarket.ro/de/blog';
    return 'https://roautomarket.ro/blog';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={getMetaLang()} />
        <title>{content.blog_title}</title>
        <meta name="description" content={content.blog_subtitle} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl()} />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content={content.blog_title} />
        <meta property="og:description" content={content.blog_subtitle} />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={content.blog_title} />
        <meta name="twitter:description" content={content.blog_subtitle} />
        <meta name="twitter:image" content="https://roautomarket.ro/og-roautomarket.png" />
      </Helmet>
      <SEOHreflang canonicalUrl="https://roautomarket.ro/blog" />

      {/* Dark Premium Hero */}
      <section className="surface-dark relative bg-brand-navy overflow-hidden !min-h-0">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy-2 to-brand-navy" />
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-5" />
        <div className="relative container mx-auto px-4 pt-2 pb-5 md:py-12">
          <Breadcrumbs items={[
            { label: content.blog_title, href: '#' }
          ]} className="mb-2 md:mb-6 [&_a]:text-brand-cream/60 [&_span]:text-brand-cream/80" />
          
          <div className="text-left md:text-center max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-brand-cream mb-2 md:mb-4 font-serif not-italic">
              {content.blog_title}
            </h1>
            <p className="text-base md:text-xl text-brand-cream/80 max-w-2xl mx-auto leading-relaxed">
              {content.blog_subtitle}
            </p>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 pt-5 pb-10 md:py-10">
        <div className="max-w-6xl mx-auto">

          {/* Active category filter indicator */}
          {activeCategory && (
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-muted-foreground">Filtrare după:</span>
              <Badge variant="default" className="gap-1">
                {activeCategory}
                <button 
                  onClick={() => setSearchParams({})} 
                  className="ml-1 hover:opacity-70"
                  aria-label="Șterge filtrul"
                >
                  ✕
                </button>
              </Badge>
            </div>
          )}

          {/* Two-column layout: Main + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
            {/* Main Content */}
            <div>

              {/* Categorii: pe crem, deasupra articolelor */}
              <div className="mb-6">
                <label htmlFor="blog-category" className="md:hidden block text-sm font-semibold mb-1.5 text-foreground">Categorie</label>
                <select
                  id="blog-category"
                  className="md:hidden w-full h-11 rounded-[4px] border border-border bg-card px-3 text-foreground text-sm font-medium"
                  value={activeBlogFilter}
                  onChange={(e) => selectCategory(e.target.value)}
                >
                  {blogCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat} ({cat !== "Toate" ? categoryCounts[cat] : blogPosts.length})</option>
                  ))}
                </select>
                <div className="hidden md:flex flex-wrap gap-2">
                  {blogCategories.map((cat) => {
                    const isActive = activeBlogFilter === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        aria-pressed={isActive}
                        className={cn(
                          "inline-flex items-center rounded-[4px] border px-3 py-1.5 text-sm font-semibold transition-colors",
                          isActive
                            ? "chip-active"
                            : "bg-card text-foreground border-border hover:border-foreground/40"
                        )}
                        onClick={() => selectCategory(cat)}
                      >
                        {cat}{cat !== "Toate" ? ` (${categoryCounts[cat]})` : ` (${blogPosts.length})`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Featured Posts */}
              {featuredPosts.length > 0 && (
                <div className="mb-12">
                  <div className="flex items-center gap-2 mb-6">
                    <TrendingUp className="text-primary" size={24} />
                    <h2 className="text-2xl md:text-3xl font-semibold font-serif">{content.blog_featured}</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {featuredPosts.map((post) => (
                      <Card key={post.slug} className="overflow-hidden hover:shadow-lg transition-all border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                        <CardHeader>
                          <div className="flex items-center gap-2 mb-3">
                            <Badge variant="secondary">{post.category}</Badge>
                            <Badge variant="outline" className="text-primary">{content.blog_featured_badge}</Badge>
                          </div>
                          <CardTitle className="text-xl md:text-2xl font-serif hover:text-primary transition-colors leading-tight">
                            <Link to={`${blogPrefix}/${post.slug}`}>{post.title}</Link>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                          <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <Calendar size={14} />
                                <span>{new Date(post.date).toLocaleDateString('ro-RO')}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                <span>{post.readTime}</span>
                              </div>
                            </div>
                            <Link 
                              to={`${blogPrefix}/${post.slug}`}
                              className="text-brand-gold hover:underline flex items-center gap-1 font-medium"
                            >
                              {content.blog_read_more || "Citește mai mult"}
                              <ArrowRight size={14} />
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Posts */}
              <div id="blog-articles">
                <h2 className="text-2xl md:text-3xl font-semibold font-serif mb-6">
                  {activeBlogFilter !== "Toate" ? activeBlogFilter : content.blog_all_articles}
                  {activeBlogFilter !== "Toate" && (
                    <span className="ml-2 text-base font-normal text-muted-foreground">
                      ({filteredBlogPosts.length})
                    </span>
                  )}
                </h2>
                {filteredBlogPosts.length === 0 ? (
                  <div className="py-12 text-center border border-dashed rounded-lg">
                    <p className="text-muted-foreground mb-4">Nu există articole în această categorie momentan.</p>
                    <button
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete("blogCategory");
                        setSearchParams(newParams);
                      }}
                      className="text-primary hover:underline font-medium text-sm"
                    >
                      Vezi toate articolele
                    </button>
                  </div>
                ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {regularPosts.map((post) => (
                    <Card key={post.slug} className="overflow-hidden hover:shadow-lg transition-all border-border/50">
                      <CardHeader>
                        <Badge variant="secondary" className="mb-3 w-fit">{post.category}</Badge>
                        <CardTitle className="text-lg md:text-xl font-serif hover:text-primary transition-colors leading-tight">
                          <Link to={`${blogPrefix}/${post.slug}`}>{post.title}</Link>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground text-sm mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Calendar size={12} />
                              <span>{new Date(post.date).toLocaleDateString('ro-RO')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock size={12} />
                              <span>{post.readTime}</span>
                            </div>
                          </div>
                          <Link 
                            to={`${blogPrefix}/${post.slug}`}
                            className="text-brand-gold hover:underline flex items-center gap-1 font-medium"
                          >
                            {content.blog_read_more || "Citește mai mult"}
                            <ArrowRight size={12} />
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                )}
              </div>

              {/* News Posts from Supabase - Only show for RO and when no category filter is active */}
              {country === 'RO' && activeBlogFilter === "Toate" && (
                <div className="mt-12">
                <NewsSection blogPrefix={blogPrefix} country="RO" category={activeCategory} />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block space-y-6">
              {/* Categories */}
              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Categorii</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {blogCategories.filter(c => c !== "Toate").map((cat) => {
                    const count = blogPosts.filter(p => p.category === cat).length;
                    if (count === 0) return null;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set("blogCategory", cat);
                          setSearchParams(newParams);
                          setTimeout(() => document.getElementById("blog-articles")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                        }}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                          activeBlogFilter === cat
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-muted text-foreground"
                        )}
                      >
                        <span>{cat}</span>
                        <Badge variant="secondary" className="text-xs">{count}</Badge>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Popular Articles */}
              {featuredPosts.length > 0 && (
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp size={16} className="text-primary" />
                      Populare
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {featuredPosts.slice(0, 4).map((post) => (
                      <Link
                        key={post.slug}
                        to={`${blogPrefix}/${post.slug}`}
                        className="block group"
                      >
                        <p className="text-sm font-medium leading-snug group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </p>
                        <span className="text-xs text-muted-foreground">{post.readTime}</span>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Newsletter CTA */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6 space-y-3">
                  <p className="font-semibold text-sm">{content.blog_newsletter_title}</p>
                  <p className="text-xs text-muted-foreground">{content.blog_newsletter_desc}</p>
                  <input 
                    type="email" 
                    placeholder={content.blog_newsletter_placeholder}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/30 bg-background"
                  />
                  <button className="w-full px-4 py-2 text-sm bg-brand-gold text-brand-navy rounded-lg hover:bg-brand-gold-light transition-colors font-medium">
                    {content.blog_newsletter_button}
                  </button>
                </CardContent>
              </Card>
            </aside>
          </div>

          {/* Mobile Newsletter CTA */}
          <Card className="mt-12 lg:hidden bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-center">{content.blog_newsletter_title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground mb-6">{content.blog_newsletter_desc}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder={content.blog_newsletter_placeholder}
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary bg-background"
                />
                <button className="px-6 py-2 bg-brand-gold text-brand-navy rounded-lg hover:bg-brand-gold-light transition-colors font-medium">
                  {content.blog_newsletter_button}
                </button>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-4">{content.blog_newsletter_privacy}</p>
            </CardContent>
          </Card>

          {/* SEO Content Section */}
          <div className="mt-12 prose prose-gray max-w-none">
            <h2 className="text-2xl md:text-3xl font-semibold font-serif mb-4">{content.blog_why_title}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold font-serif mb-3">{content.blog_why_buyers}</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ {content.blog_why_buyers_1}</li>
                  <li>✓ {content.blog_why_buyers_2}</li>
                  <li>✓ {content.blog_why_buyers_3}</li>
                  <li>✓ {content.blog_why_buyers_4}</li>
                  <li>✓ {content.blog_why_buyers_5}</li>
                  <li>✓ {content.blog_why_buyers_6}</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold font-serif mb-3">{content.blog_why_sellers}</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ {content.blog_why_sellers_1}</li>
                  <li>✓ {content.blog_why_sellers_2}</li>
                  <li>✓ {content.blog_why_sellers_3}</li>
                  <li>✓ {content.blog_why_sellers_4}</li>
                  <li>✓ {content.blog_why_sellers_5}</li>
                  <li>✓ {content.blog_why_sellers_6}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default Blog;
